/**
 * Sprint D — Card de verificação externa de providers.
 * Lista providers cujo `verification_status != 'not_required'` e permite
 * ao admin marcar como `verification_confirmed` (que também reativa o
 * provider e devolve prioridade decente).
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

type VerificationStatus =
  | "not_required"
  | "verification_missing"
  | "verification_pending"
  | "verification_confirmed"
  | "blocked";

interface ProviderRow {
  id: string;
  provider_name: string;
  active: boolean;
  priority: number;
  verification_status: VerificationStatus;
  verification_blocking: boolean;
  verification_notes: string | null;
}

const STATUS_META: Record<VerificationStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  not_required: { label: "Não requer", variant: "outline" },
  verification_missing: { label: "Verificação faltando", variant: "destructive" },
  verification_pending: { label: "Aguardando verificação", variant: "secondary" },
  verification_confirmed: { label: "Confirmada", variant: "default" },
  blocked: { label: "Bloqueado", variant: "destructive" },
};

export function ProviderVerificationCard() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["provider-verifications"],
    queryFn: async (): Promise<ProviderRow[]> => {
      const { data, error } = await supabase
        .from("syra_ai_providers_safe")
        .select("id,provider_name,active,priority,verification_status,verification_blocking,verification_notes")
        .neq("verification_status", "not_required")
        .order("provider_name");
      if (error) throw error;
      return (data ?? []) as ProviderRow[];
    },
    refetchInterval: 30_000,
  });

  const confirmMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("syra_ai_providers")
        .update({
          verification_status: "verification_confirmed",
          verification_blocking: false,
          verification_updated_at: new Date().toISOString(),
          active: true,
          priority: 5,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Verificação confirmada — provider reativado.");
      qc.invalidateQueries({ queryKey: ["provider-verifications"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rows = data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <CardTitle className="text-base">Verificações externas pendentes</CardTitle>
            <CardDescription className="text-xs">
              Providers que dependem de ação manual em painéis externos antes de serem usados em produção.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {!isLoading && rows.length === 0 && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="w-4 h-4 text-primary" />
            Nenhuma verificação externa pendente.
          </div>
        )}

        {rows.map((r) => {
          const meta = STATUS_META[r.verification_status];
          return (
            <div key={r.id} className="rounded-md border p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{r.provider_name}</span>
                  <Badge variant={meta.variant} className="text-[10px]">{meta.label}</Badge>
                  {r.verification_blocking && (
                    <Badge variant="outline" className="text-[10px] border-destructive text-destructive">
                      bloqueia uso
                    </Badge>
                  )}
                </div>
                <Badge variant={r.active ? "default" : "outline"} className="text-[10px]">
                  {r.active ? "ativo" : "desativado"} · p={r.priority}
                </Badge>
              </div>
              {r.verification_notes && (
                <p className="text-xs text-muted-foreground flex items-start gap-1">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{r.verification_notes}</span>
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                {r.provider_name.toLowerCase().includes("openai") && (
                  <a
                    href="https://platform.openai.com/settings/organization/general"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Abrir painel da OpenAI <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <a
                  href="/docs/OPENAI_VERIFICATION_CHECKLIST.md"
                  className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  Ver checklist
                </a>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={confirmMut.isPending}
                  onClick={() => confirmMut.mutate(r.id)}
                >
                  Marcar como verificado e reativar
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
